import { makeAutoObservable } from 'mobx'
import { productMobx, storeMobx } from 'restaurant'
import { memberMobx } from '@mobxStore'
import { VoiceAPI } from '@api/index'
import { ddayjs } from '@dt/fe-utils'
import { memberCardMobx } from '@mobx/mkt'
import { wxConfigG } from 'controllers/third'
import { mallProductMobx } from '@mobx/mall'

class VoiceServiceMobx {
  constructor () {
    makeAutoObservable(this, {}, {
      autoBind: true
    })
  }

  /** 搜索列表 */
  searchList = []

  /** 开启服务铃 */
  get isOpendServiceRing () {
    return storeMobx.info.openSendServiceRing
  }

  /** 开启语音搜索 餐品 */
  get isOpenSearchProduct () {
    return storeMobx.info.voiceConfig?.canSearchProduct
  }

  /** 开启语音搜索 活动 */
  get isOpenSearchActivity () {
    return storeMobx.info.voiceConfig?.canSearchActivity
  }

  /** 语音服务是否可用 */
  get isVoiceEnable () {
    return (wxConfigG.isInited() || (navigator as any)?.getUserMedia || (navigator as any)?.mediaDevices?.getUserMedia)
  }

  /** 语音搜索是否可用 */
  get isVoiceSearchEnable () {
    const isOpen = this.isOpenSearchProduct || this.isOpenSearchActivity
    return !!(isOpen && this.isVoiceEnable)
  }

  /** 搜索列表餐品处理 */
  get searchedProducts (): IViewProduct[] | null {
    const _search = []
    if (this.searchList.length) {
      const productList = this.searchList.filter(item => item.content_type === 'product')
      for (let i = 0; i < productMobx.viewProducts.length; i++) {
        const product = productMobx.viewProducts[i]
        for (let j = 0; j < productList.length; j++) {
          const searchItem = productList[j]
          if (searchItem.content_id === product.no && product.isNotSingleInCombo && product.isShowInMenu) {
            _search.push({
              ...product,
              score: searchItem.score
            })
            break
          }
        }
      }

      return _search.sort((a, b) => b.score - a.score)
    }
    return _search
  }

  /** 语音匹配 */
  async voiceMatch (content) {
    const res = await VoiceAPI.voiceMatch(content)
    if (res.content) {
      this.setSearchList(res.content)
    }
    return res
  }

  /** 内容匹配 */
  async contentMatch (content) {
    const res = await VoiceAPI.contentMatch(content)
    return res
  }

  /** 获取搜索餐品 */
  getSearchedProducts (list: any[]): IViewProduct[] | null {
    const _search = []
    if (list.length) {
      const productList = list.filter(item => item.content_type === 'product')
      for (let i = 0; i < productMobx.viewProducts.length; i++) {
        const product = productMobx.viewProducts[i]
        for (let j = 0; j < productList.length; j++) {
          const searchItem = productList[j]
          if (searchItem.content_id === product.no && product.isNotSingleInCombo && product.isShowInMenu) {
            _search.push({
              ...product,
              score: searchItem.score
            })
            break
          }
        }
      }

      return _search.sort((a, b) => b.score - a.score)
    }
    return _search
  }

  /** 获取搜索商品(商城) */
  getSearchedMallProducts (list: any[]): IMallViewProduct[] | null {
    const _search = []
    if (list.length) {
      const productList = list.filter(item => item.content_type === 'mallProduct')
      for (let i = 0; i < mallProductMobx.viewProducts.length; i++) {
        const product = mallProductMobx.viewProducts[i]
        for (let j = 0; j < productList.length; j++) {
          const searchItem = productList[j]
          if (searchItem.content_id === product.no) {
            _search.push({
              ...product,
              score: searchItem.score
            })
            break
          }
        }
      }

      return _search.sort((a, b) => b.score - a.score)
    }
    return _search
  }

  /** 获取今天的有效时段 */
  getTodayRanges (act) {
    const nowWeekDay = ddayjs().day()
    const { timeRanges } = act

    // 获取今天的有效时段
    const todayRanges = (timeRanges || []).filter(timeRange => {
      const { weekDay, isOpen, ranges } = timeRange
      return weekDay === nowWeekDay && isOpen
    })

    return todayRanges?.[0]?.ranges || []
  }

  /** 获取可用时间段 */
  getAvailableActsTime (act) {
    const nowTime = ddayjs().format('HH:mm')
    const { isAllTime } = act
    const todayRanges = this.getTodayRanges(act)

    // 获取今天的有效时段
    const isTimeAvailable = todayRanges.some(({ startTime, endTime }, index) => {
      return startTime <= nowTime && endTime >= nowTime
    })

    // 有效时段内
    const isTime = isAllTime || !isAllTime && isTimeAvailable

    return isTime
  }

  /** 获取可参与用户 */
  getAvailableActsMember (act, myCards) {
    const { isAllMember, memberCards } = act
    const isMemberAvailable = myCards.some(card => {
      const { cardId, status } = card
      return status === 'enable' && memberCards.includes(cardId)
    })

    // 可参与用户
    const isMember = isAllMember || !isAllMember && isMemberAvailable

    return isMember
  }

  getAvailableActsMemberList (act) {
    const cards = memberMobx.cardListForMy
    return cards
  }

  async getAvailableActsMap (list: any[] = []) {
    // 当前时间字符串
    const nowDate = ddayjs().format('YYYY-MM-DD')

    const myCards = await memberMobx.getMemberCards() || []
    const availableActsMap = list.filter((act) => {
      const { startTime, endTime, business } = act
      const startDate = ddayjs(startTime).format('YYYY-MM-DD')
      const endDate = ddayjs(endTime).format('YYYY-MM-DD')

      const isDate = startDate <= nowDate && endDate >= nowDate // 有效期内
      const isTime = this.getAvailableActsTime(act) // 有效时段内
      const isMember = this.getAvailableActsMember(act, myCards) // 可参与用户
      const isBusiness = business === 'meal' // 用餐业务

      return isDate && isTime && isMember && isBusiness
    })

    return availableActsMap
  }

  /** 获取活动列表 */
  async getActsList (list: any[] = []) {
    // 当前时间字符串
    const nowDate = ddayjs().format('YYYY-MM-DD')
    const nowTime = ddayjs().format('HH:mm')
    const nowWeekDay = ddayjs().day()

    const myCards = await memberMobx.getMemberCards() || []

    let enableActs = [], disableActs = []
    list.forEach((act) => {
      const { startTime, endTime, isAllTime, timeRanges, isAllMember, memberCards, business } = act
      const startDate = ddayjs(startTime).format('YYYY-MM-DD')
      const endDate = ddayjs(endTime).format('YYYY-MM-DD')

      const isMemberAvailable = myCards.some(card => {
        const { cardId, status } = card

        return status === 'enable' && memberCards.includes(cardId)
      })

      // 获取今天的有效时段
      const todayRanges = [] as any[]
      (timeRanges || []).forEach(timeRange => {
        const { weekDay, isOpen, ranges } = timeRange
        if (weekDay === nowWeekDay && isOpen) {
          todayRanges.push(...ranges)
        }
      })

      const isTimeAvailable = todayRanges.some(({ startTime, endTime }, index) => {
        return startTime <= nowTime && endTime >= nowTime
      })

      const isDate = startDate <= nowDate && endDate >= nowDate // 有效期内
      const isTime = isAllTime || !isAllTime && isTimeAvailable // 有效时段内
      const isMember = isAllMember || !isAllMember && isMemberAvailable // 可参与用户
      const isBusiness = business === 'meal' // 用餐业务
      const cardsName = []
      memberCards.forEach(cardId => {
        memberCardMobx.cardList.forEach(card => {
          if (card.cardId === cardId) {
            cardsName.push(card.cardName)
          }
        })
      })

      if (isDate && isBusiness) {
        let info = {
          ...act,
          cardsName
        }

        if (!isTime || !isMember) {
          disableActs.push({
            ...info,
            isDisable: true,
            disableReason: !isTime ? 'time' : 'member',
          })
        } else {
          enableActs.push({
            ...info,
            isDisable: false,
          })
        }
      }
    })

    return enableActs.concat(disableActs)
  }

  /** 获取生效活动餐品 */
  getAvailableActsProducts (list: any[]): IViewProduct[] | null {
    let availableProducts = []
    if (list.length) {
      for (let i = 0; i < productMobx.viewProducts.length; i++) {
        const product = productMobx.viewProducts[i]
        for (let j = 0; j < list.length; j++) {
          const searchItem = list[j]
          if (product.no === searchItem.productNo) {
            availableProducts.push(product)
          }
        }
      }
    }
    return availableProducts
  }

  setSearchList (list: any[]) {
    this.searchList = list || []
  }
}

export default new VoiceServiceMobx()
